/** The match types the Search & Merge screen offers. */
export const MATCH_TYPES = [
  { value: 'exact', label: 'Exact' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'contains', label: 'Contains' },
]

// The most values one field is searched for at once.
export const MAX_VALUES = 10
// The most fields one search ANDs together.
export const MAX_FIELDS = 5
// The most characters one search's values can add up to. Salesforce runs a
// query of up to 100,000 characters, and escaping can double a value, so this
// leaves room for that and for the rest of the query. Each field's own limit
// keeps a search far under it; this is for a field whose length isn't known.
export const MAX_SEARCH_CHARACTERS = 40000

const NUMBER_TYPES = new Set(['int', 'long', 'double', 'currency', 'percent'])
// The SOQL operators Search & Merge sends: Exact, and Starts With and Contains.
const SEARCH_OPERATORS = ['=', 'LIKE']

/**
 * Whether Salesforce can search on the field at all, as its `soqlOperators`
 * say. A field without that list isn't offered: its type can't say - a text
 * field Salesforce won't filter on looks like any other.
 */
export function isFilterable({ soqlOperators } = {}) {
  return soqlOperators?.some(op => SEARCH_OPERATORS.includes(op)) ?? false
}

/**
 * Whether the field can take Starts With and Contains: only where its
 * `soqlOperators` say Salesforce accepts LIKE.
 */
export function canMatchPart({ soqlOperators } = {}) {
  return soqlOperators?.includes('LIKE') ?? false
}

/**
 * Which value input suits the field: `number`, `boolean`, `date`, `datetime`,
 * `time`, `picklist`, `multipicklist` or `text`.
 */
export function inputKind({ type } = {}) {
  if (NUMBER_TYPES.has(type)) return 'number'
  const kinds = [
    'boolean',
    'date',
    'datetime',
    'time',
    'picklist',
    'multipicklist',
  ]
  return kinds.includes(type) ? type : 'text'
}

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/
const NUMBER_PARTS = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/
// Further than any field's digits reach; past it the value is left for cdapi
// to refuse rather than written out at length.
const MAX_EXPONENT = 100

/**
 * A number box's value as cdapi takes a number: plain digits. The box itself
 * also accepts `+5`, `.5`, `5.` and `1e5`, which cdapi refuses. They are
 * rewritten as text, never through a float, so every digit typed is sent;
 * anything else is sent as typed for cdapi to refuse.
 */
export function plainNumber(value) {
  if (!value || PLAIN_NUMBER.test(value)) return value
  const parts = NUMBER_PARTS.exec(value)
  if (!parts) return value
  const [, sign, whole, fraction = '', exponent = '0'] = parts
  const shift = Number(exponent)
  if (!(whole || fraction) || Math.abs(shift) > MAX_EXPONENT) return value

  const digits = whole + fraction
  const point = whole.length + shift
  const integer = point <= 0 ? '0' : digits.slice(0, point).padEnd(point, '0')
  const decimals =
    point <= 0 ? '0'.repeat(-point) + digits : digits.slice(point)
  const plain = [integer.replace(/^0+(?=\d)/, ''), decimals.replace(/0+$/, '')]
    .filter(Boolean)
    .join('.')
  return sign === '-' && /[1-9]/.test(plain) ? `-${plain}` : plain
}

// The screen's match types as the operators a stored criteria carries: cdapi's
// filter suffixes without the underscore, which is also what the Audit Log
// payload sends back.
const OP_BY_MATCH_TYPE = {
  exact: 'eq',
  starts_with: 'starts_with',
  contains: 'contains',
}

// How each operator reads in front of a field name.
const OP_WORDING = {
  eq: 'Exact match on',
  ne: 'Not equal to',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  contains: 'Contains match on',
  in: 'One of',
}

/**
 * A search as the criteria shape the Audit Log receives, so a local result
 * grid and a merged set describe themselves the same way. The value is left
 * out, as the server leaves it out.
 */
export function criteriaFromSearch(blocks) {
  return {
    groups: blocks.map(({ field, fieldLabel, conditions }) =>
      conditions.map(({ matchType }) => ({
        field,
        field_label: fieldLabel || field,
        op: OP_BY_MATCH_TYPE[matchType],
      })),
    ),
  }
}

function conditionText({ field, field_label: fieldLabel, op }) {
  const name = fieldLabel || field
  // Own keys only: an op like `constructor` would otherwise find a function.
  const wording = (Object.hasOwn(OP_WORDING, op) && OP_WORDING[op]) || op
  // An operator we can't read still leaves the field worth showing.
  return wording ? `${wording} ${name}` : name
}

// A condition worth a word: something with a field to name.
function isReadableCondition(condition) {
  return (
    !!condition &&
    typeof condition === 'object' &&
    !!(condition.field_label || condition.field)
  )
}

/**
 * One field's values - a group whose conditions are all on the same field -
 * read as the field, with a count when there are several: "Contains match on
 * Name (2 values)", or "Multiple match types on Name (2 values)" when they
 * differ. A group mixing fields, which only a hand-built search can send,
 * spells out its conditions instead.
 */
function groupText(group, groupCount) {
  if (new Set(group.map(c => c.field || c.field_label)).size > 1) {
    const text = group.map(conditionText).join(' or ')
    return groupCount > 1 ? `(${text})` : text
  }
  if (group.length === 1) return conditionText(group[0])

  const values = `(${group.length} values)`
  if (new Set(group.map(c => c.op)).size > 1) {
    const { field, field_label: fieldLabel } = group[0]
    return `Multiple match types on ${fieldLabel || field} ${values}`
  }
  return `${conditionText(group[0])} ${values}`
}

/**
 * What a Search & Merge set shows where a Match Score normally sits: each
 * field's values, the fields joined with "and" - "Contains match on Name (2
 * values) and Exact match on State". `summary` is cut short by the header and
 * `detail`, its tooltip, shows it whole. `null` when there are no conditions -
 * cdserver sends none for criteria it can't read - which the caller shows as a
 * plain label rather than a blank.
 */
export function describeCriteria(criteria) {
  // Anything malformed is dropped rather than thrown on: this renders in
  // every set's header, so one bad row would take the whole grid down.
  const groups = (Array.isArray(criteria?.groups) ? criteria.groups : [])
    .map(group =>
      Array.isArray(group) ? group.filter(isReadableCondition) : [],
    )
    .filter(group => group.length)
  if (!groups.length) return null

  const text = groups
    .map(group => groupText(group, groups.length))
    .join(' and ')
  return { summary: text, detail: text }
}

// The key the charts file every Search & Merge Director under. Not a real rule
// id, so the name and objects come from `foldSearchMerge`, not the metadata.
export const SEARCH_MERGE_CHART_KEY = '__search_merge__'

/**
 * Chart entries with every Search & Merge Director folded into one, at the end.
 *
 * There is one of our Directors per (object, Layout), but the user sees one
 * feature, so the charts show one "Search & Merge" entry. Which entries are
 * ours comes from the `origin` flag `/dashboard/metadata` puts on them.
 * `countKeys` are summed; nothing flagged leaves the entries as they were.
 */
export function foldSearchMerge(entries, directors, countKeys) {
  const isOurs = entry => directors?.[entry.director]?.origin === 'search_merge'
  const ours = (entries || []).filter(isOurs)
  if (!ours.length) return entries

  const folded = { director: SEARCH_MERGE_CHART_KEY }
  countKeys.forEach(key => {
    folded[key] = ours.reduce((total, entry) => total + (entry[key] || 0), 0)
  })
  return [...entries.filter(entry => !isOurs(entry)), folded]
}

/** The metadata the folded entry is labelled with. */
export function searchMergeChartDirector(directors) {
  const objects = Object.values(directors || {})
    .filter(director => director.origin === 'search_merge')
    .flatMap(director => director.objects || [])
  return {
    name: 'Search & Merge',
    objects: [...new Set(objects)],
    origin: 'search_merge',
  }
}

/**
 * The filters a grid shows. Origin narrows nothing until a first Search &
 * Merge creates its agent, which can happen after the grid has loaded its
 * filters, so this follows the session rather than being decided once.
 */
export function shownFilters(filters, session) {
  return filters.filter(
    filter =>
      filter.code !== 'origin' ||
      (session.searchMergeFlagEnabled && !!session.searchMergeAgentId),
  )
}

/**
 * The Audit Log filters as they apply. Business Rule and Match Score describe
 * rule-based sets, so with Origin on they are cleared: a rule picked before
 * Origin was turned on is not sent, and doesn't go on shaping the advanced
 * filters' field list or counting as an applied filter.
 */
export function withOriginApplied(filters) {
  if (!filters?.origin?.length) return filters
  return { ...filters, segment: [], mcs_category: [] }
}

/**
 * One merged or queued sets chart's bars. Search & Merge never auto-merges, so
 * its charts drop the Automatic bar, and each remaining bar keeps its own
 * colour - `colors` is ordered Manual, Automatic, Undo like every other chart.
 */
export function dupeSetChartBars(
  { manual, auto, undo, undoLabel },
  colors,
  isSearchMerge,
) {
  if (isSearchMerge) {
    return {
      data: [manual, undo],
      colors: [colors[0], colors[2]],
      categories: ['Manual', undoLabel],
    }
  }
  return {
    data: [manual, auto, undo],
    colors,
    categories: ['Manual', 'Automatic', undoLabel],
  }
}
